/** The platform's own voice — German speech synthesised on our server.
 *
 * Runs on our container rather than a paid cloud voice, which is why there
 * is no quota here and no credit cost. The point of preferring it over the
 * browser's built-in synthesis is consistency: `speechSynthesis` gives a
 * different voice on every device, and on an iPhone it is whatever Apple
 * happens to have installed.
 */
import { API_BASE, ApiError, getTokens, request } from "./http";

export type VoiceId = "female" | "male";

export interface VoiceStatus {
  /** False on a deployment with no voice models — fall back to the browser. */
  configured: boolean;
  voices: VoiceId[];
  preferred: VoiceId;
  max_chars: number;
  /** Whether the server can LISTEN as well as speak. False (or absent, on
   *  an older server) means the browser's own recogniser does the
   *  listening, exactly as before. */
  transcription?: boolean;
  max_clip_seconds?: number;
}

export interface Transcript {
  text: string;
  seconds: number;
  model: string;
}

export function voiceStatus(): Promise<VoiceStatus> {
  return request("/speech/voices");
}

/** Send one recorded turn to the server and get its words back.
 *
 * Multipart rather than JSON, so `request()` is not used; it shares
 * API_BASE and the bearer token with everything else so the two cannot
 * drift on where the API lives or how it is authenticated. Failures are
 * surfaced as ApiError with the server's own message, because a 402 here
 * ("top up to keep talking") is something the learner needs to read, not
 * a generic "could not transcribe".
 */
export async function transcribe(
  clip: Blob,
  opts: { filename: string; language: string; seconds: number },
): Promise<Transcript> {
  const { access } = getTokens();
  const headers: Record<string, string> = {};
  if (access) headers.Authorization = `Bearer ${access}`;
  const form = new FormData();
  form.append("audio", clip, opts.filename);
  form.append("language", opts.language);
  form.append("seconds", String(opts.seconds));

  const res = await fetch(`${API_BASE}/speech/transcribe`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let detail = `transcription failed: ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      // No readable body; the status is all we know.
    }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Synthesise `text` and return the audio.
 *
 * Not `request()`: that helper parses the body as JSON and this one is an
 * MP3. It reuses API_BASE and the same bearer token so the two cannot drift
 * apart on where the API lives or how it is authenticated.
 */
export async function say(text: string, voice?: VoiceId | null): Promise<Blob> {
  const { access } = getTokens();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(`${API_BASE}/speech/say`, {
    method: "POST",
    headers,
    body: JSON.stringify({ text, voice: voice ?? null }),
  });
  // Every failure is the same to a caller: it could not speak, so use the
  // browser's voice instead. 503 (no models) is the expected one.
  if (!res.ok) throw new Error(`speech unavailable: ${res.status}`);
  return res.blob();
}
